import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiBase } from '../utils/authHeaders';
import { fmtUnitCost } from '../utils/unitCost';
import { formatMoney } from '../utils/money';
import {
  capabilitiesAllow, errorMessage, readMktScope, requestConfig, scopeKey, useMktScope,
} from './MarketplaceShared';

export {
  capabilitiesAllow, errorMessage, isCancelled, scopeKey, formatWhen, statusLabel, newUuid,
  useStableIds, Field, Table, Messages,
} from './MarketplaceShared';

// Supplier price lists: backend/supplier_offers (/api/supplier-offers). One screen for
// every source format: a batch from a CSV upload and one pulled from Xero bills share
// one shape, so nothing below branches on `source_format` except its display label.

export const spApi = axios.create({ baseURL: `${apiBase()}/api/supplier-offers` });
export const readSpScope = readMktScope;
export const useSpScope = useMktScope;

// Mirrors REQUIRED_COLUMNS / OPTIONAL_COLUMNS / SUPPORTED_UNITS / SUPPORTED_CURRENCIES
// in backend/supplier_offers/imports.py. The server is the authority; this is help text.
export const REQUIRED_COLUMNS = ['supplier_sku', 'description', 'pack_unit'];
export const OPTIONAL_COLUMNS = ['pack_quantity', 'pack_price', 'currency', 'tax_basis', 'valid_from', 'valid_to', 'quoted_at'];
export const SUPPORTED_UNITS = ['g', 'kg', 'ml', 'l', 'unit', 'each', 'case', 'box', 'pack'];
export const SUPPORTED_CURRENCIES = ['GBP', 'EUR', 'USD'];

export const XERO_PULL_PATH = '/sources/xero-bills/pull';

const FORMAT_LABELS = { csv: 'CSV price list', xero_bills: 'Xero bills' };
export const formatLabel = f => FORMAT_LABELS[f] || (f ? String(f).replaceAll('_', ' ') : 'Unknown source');

const PENDING_LABELS = { ambiguous_mapping: 'Pick ingredient', changed_pack: 'Pack or currency changed' };
export const pendingLabel = r => PENDING_LABELS[r] || String(r || '').replaceAll('_', ' ');

export function useSupplierPriceListsNavEnabled(token, probe = true) {
  const scope = useSpScope(token);
  const key = scopeKey(scope);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (probe && scope.orgId && scope.token) {
      spApi.get('/capabilities', requestConfig(scope, controller.signal))
        .then(res => { if (!controller.signal.aborted && capabilitiesAllow(res?.data)) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope, probe]);
  return allowedKey === key;
}

/** Request/mutation runtime for a workspace remounted per scope (same contract as useMktRuntime). */
export function useSpRuntime(scope) {
  const lifetime = useRef(new AbortController());
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => controller.abort();
  }, []);
  const active = useCallback(
    () => !lifetime.current.signal.aborted && scopeKey(readSpScope()) === scopeKey(scope),
    [scope],
  );
  const request = useCallback(async (method, path, { body, params, external } = {}) => {
    const config = { ...requestConfig(scope, lifetime.current.signal) };
    if (params) config.params = params;
    if (external) config.baseURL = apiBase();
    const res = method === 'get' ? await spApi.get(path, config) : await spApi[method](path, body, config);
    return res.data;
  }, [scope]);
  const run = useCallback(async (work, success) => {
    if (lock.current || !active()) return undefined;
    lock.current = true;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await work();
      if (active() && success) setNotice(success);
      return { ok: true, result };
    } catch (e) {
      if (active()) setError(errorMessage(e));
      return { ok: false, error: e };
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }, [active]);
  return { active, request, run, busy, error, notice, setError, setNotice };
}

export const asItems = v => (Array.isArray(v) ? v : Array.isArray(v?.items) ? v.items : []);

const num = v => (v === null || v === undefined || v === '' ? null : Number(v));

/** Pack price as quoted, e.g. "£12.50 / 5 kg". Unknown stays unknown, never £0. */
export function packPrice(row) {
  const price = num(row?.pack_price);
  if (price === null || Number.isNaN(price)) return 'Unknown';
  const currency = row.currency || 'GBP';
  const amount = formatMoney(price, currency);
  const qty = num(row.pack_quantity);
  return qty ? `${amount} / ${row.pack_quantity} ${row.pack_unit}` : `${amount} / ${row.pack_unit || 'pack'}`;
}

/** Price per pack unit (number) or null when quantity or price is unknown. */
export function unitPrice(row) {
  const price = num(row?.pack_price);
  const qty = num(row?.pack_quantity);
  if (price === null || !qty || Number.isNaN(price) || Number.isNaN(qty)) return null;
  return price / qty;
}

/** Per-unit price through the shared formatter (£/kg, £/L, £/each ...). */
export function perUnit(row) {
  const value = unitPrice(row);
  if (value === null) return '—';
  if ((row.currency || 'GBP') !== 'GBP') return `${row.currency} ${value.toFixed(2)}/${row.pack_unit}`;
  return fmtUnitCost(value, row.pack_unit);
}

/** Change vs the currently published offer for the same SKU, comparable only per same unit. */
export function priceChange(row, current) {
  if (!current) return { label: 'New line', tone: 'new' };
  if (current.pack_unit !== row.pack_unit || (current.currency || 'GBP') !== (row.currency || 'GBP')) {
    return { label: `Was ${perUnit(current)} (different unit)`, tone: 'warn' };
  }
  const before = unitPrice(current);
  const after = unitPrice(row);
  if (before === null || after === null) return { label: `Was ${packPrice(current)}`, tone: '' };
  if (Math.abs(after - before) < 1e-9) return { label: 'No change', tone: '' };
  const pct = before ? ((after - before) / before) * 100 : null;
  const sign = after > before ? '+' : '';
  return {
    label: `Was ${perUnit(current)}${pct === null ? '' : ` (${sign}${pct.toFixed(1)}%)`}`,
    tone: after > before ? 'up' : 'down',
  };
}
